/**
 * @file    CprRoutes.tsx
 * @scope   CPR
 * @summary Renders the Cross-Program Rollup sub-views (Manage Programs / Partition Filters /
 *          Program Filters) selected by the active TabNavigator tab.
 * @consumes CprContext, CPR ProgramsManager, Enterprise filter editors, DiyDrawer navigation state
 * @produces JSX for exactly one CPR sub-view at a time (or nothing on other tabs)
 * @sideEffects Writes to the CPR context and to the drawer navigation state via callbacks.
 * @notes Mirrors EnterpriseRoutes. The filter editors are the Enterprise ones, fed through the
 *        adapters in `programsManagerModel`.
 */
import React from 'react';
import ProgramsManager from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/Views/ProgramsManager/ProgramsManager';
import {
  CprPartitionFiltersTab,
  CprProgramFiltersTab,
  ProgramsManagerTab,
  toPartitionFiltersUserFields,
  toProgramFilterField,
} from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/Views/ProgramsManager/programsManagerModel';
import { useProgramCatalog } from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/Views/ProgramsManager/useProgramCatalog';
import { useCprContext } from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/state/CprContext';
import PartitionFiltersManager from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/PartitionFiltersManager/PartitionFiltersManager';
import FiltersSettingsManager from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/FiltersSettingsManager/FiltersSettingsManager';
import { useCprNavigation } from '../plugins/cpr/CprNavigationContext';
import { EngineRoutesProps } from '../plugins/types';

const CprRoutes: React.FC<EngineRoutesProps> = ({
  currentTab,
  setCurrentTab,
  goToMainTab,
  endpoint,
  availableCollections,
}) => {
  const { cprProgramsContext, setCprProgramsContext } = useCprNavigation();
  const { state, dispatch } = useCprContext();

  const rollup = state.metrics.find((m) => m.id === cprProgramsContext?.rollupId);
  const programGuids = (rollup?.programs ?? []).map((p) => p.dataCollectionGuid);
  const catalog = useProgramCatalog(endpoint, availableCollections, programGuids);
  if (!cprProgramsContext || !rollup) return null;

  const programIndex = rollup.programs.findIndex((p) => p.id === cprProgramsContext.programId);
  const program = programIndex >= 0 ? rollup.programs[programIndex] : null;

  const backToPrograms = () => setCurrentTab(ProgramsManagerTab);

  const handleCancel = () => {
    dispatch({ type: 'UPDATE_ROLLUP_METRIC', id: rollup.id, changes: cprProgramsContext.snapshot });
    setCprProgramsContext(null);
    goToMainTab();
  };

  const handleApply = () => {
    setCprProgramsContext(null);
    goToMainTab();
  };

  const openPartitionFilters = () => {
    setCprProgramsContext({ ...cprProgramsContext, programId: null, focusedFilterId: null });
    setCurrentTab(CprPartitionFiltersTab);
  };

  const openProgramFilters = (programId: string, filterId: string | null) => {
    setCprProgramsContext({ ...cprProgramsContext, programId, focusedFilterId: filterId });
    setCurrentTab(CprProgramFiltersTab);
  };

  return (
    <>
      {currentTab.name === ProgramsManagerTab.name && (
        <ProgramsManager
          rollupId={rollup.id}
          catalog={catalog}
          expandedProgramId={cprProgramsContext.programId}
          defaultActiveTab={cprProgramsContext.programId ? 'filters' : undefined}
          onCancel={handleCancel}
          onApply={handleApply}
          onManagePartitionFilters={openPartitionFilters}
          onEditFilter={openProgramFilters}
          // TODO: route to the rollup advanced settings view once it exists.
          onAdvancedSettings={() => undefined}
        />
      )}

      {currentTab.name === CprPartitionFiltersTab.name && (
        <PartitionFiltersManager
          onBack={backToPrograms}
          selectedUserFields={toPartitionFiltersUserFields(rollup)}
          dataSourceDisplayName={rollup.displayName}
          onUserFieldsChange={(fields) =>
            dispatch({
              type: 'UPDATE_ROLLUP_METRIC',
              id: rollup.id,
              changes: { partitionFilters: fields.partitionFilters ?? [] },
            })
          }
          focusedFilterId={cprProgramsContext.focusedFilterId}
          availableFilterFields={catalog.partitionFilterFields}
        />
      )}

      {currentTab.name === CprProgramFiltersTab.name && program && (
        <FiltersSettingsManager
          onBack={backToPrograms}
          selectedField={toProgramFilterField(program, programIndex)}
          onFieldChange={(field) =>
            dispatch({
              type: 'UPDATE_ROLLUP_METRIC',
              id: rollup.id,
              changes: {
                programs: rollup.programs.map((p) =>
                  p.id === program.id ? { ...p, filters: field.filters ?? [] } : p
                ),
              },
            })
          }
          focusedFilterId={cprProgramsContext.focusedFilterId}
          availableFilterFields={catalog.getFilterFields(program.dataCollectionGuid)}
        />
      )}
    </>
  );
};

export default CprRoutes;
